import { ComponentChildren } from 'preact';
import keys from 'ramda/src/keys';
import { stringLiteralArray } from '../utils/UNSAFE_arrayUtils';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';

export const headingElementTypes = stringLiteralArray(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

type ElementType = typeof headingElementTypes[number];

type HeadingProps = {
  /**
   * Specifies the heading tag
   */
  as: ElementType;

  /**
   * Specifies the text "style". Sets font size and line height.   */

  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'inherit';

  /**
   * Specifies the header id
   */
  id?: string;

  /**
   * Specifies the children
   */
  children: ComponentChildren;
};

const headingsizeMap: { [headingType in ElementType]: string } = {
  h1: '2xl',
  h2: 'xl',
  h3: 'lg',
  h4: 'md',
  h5: 'sm',
  h6: 'xs'
};

const styles = {
  base: cssProps`${{
    margin: 0
  }}`,
  size: {
    xs: cssProps`${{
      fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-xs-font-size)',
      fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-xs-font-weight)',
      lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-xs-line-height)'
    }}`,
    sm: cssProps`${{
      fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-sm-font-size)',
      fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-sm-font-weight)',
      lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-sm-line-height)'
    }}`,
    md: cssProps`${{
      fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-md-font-size)',
      fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-md-font-weight)',
      lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-md-line-height)'
    }}`,
    lg: cssProps`${{
      fontSize: `var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-lg-font-size)`,
      fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-lg-font-weight)',
      lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-lg-line-height)'
    }}`,
    xl: cssProps`${{
      fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-xl-font-size)',
      fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-xl-font-weight)',
      lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-xl-line-height)'
    }}`,
    '2xl': cssProps`${{
      fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-2xl-font-size)',
      fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-2xl-font-weight)',
      lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-typography-heading-2xl-line-height)'
    }}`,
    inherit: cssProps`${{
      fontSize: 'inherit',
      fontWeight: 'inherit',
      lineHeight: 'inherit'
    }}`
  },

  childrenMargins: cssProps`${{
    /* @ts-ignore */
    '& > *': {
      marginTop: '0 !important',
      marginBottom: '0 !important',
      marginLeft: '0 !important',
      marginRight: '0 !important'
    }
  }}`
};

export const sizeOptions = keys(styles.size);

const interpolations = [
  () => ({ class: styles.base }),
  ({ size = '2xl' }: Pick<HeadingProps, 'size'>) => ({
    class: styles.size[size]
  })
];

const styleInterpolations = mergeInterpolations<Omit<HeadingProps, 'children'>>(interpolations);

export function Heading({ children, ...props }: HeadingProps) {
  const Component = props.as;
  //@ts-ignore
  props.size = props.size ?? headingsizeMap[Component];
  const { class: cls, ...style } = styleInterpolations(props);
  return (
    <Component className={cls} style={style} id={props.id}>
      <span className={styles.childrenMargins}>{children}</span>
    </Component>
  );
}
